import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, Client, ClientStrategyLandscape, Strategy, ClientPosition, InterestLevel, Sentiment } from '../lib/api';

const STRATEGY_LABELS: Record<Strategy, string> = {
  low_volatility: 'Low Volatility',
  sp500_tilts: 'S&P 500 Tilts',
  sector_rotation: 'Sector Rotation',
  dynamic_hedge: 'Dynamic Hedge',
  risk_overlay: 'Risk Overlay',
  core_equity: 'Core Equity',
  novel: 'Novel',
};

const POSITION_LABELS: Record<ClientPosition, string> = {
  using_it: 'Using',
  considering_it: 'Considering',
  asking_about_it: 'Asking',
  pushed_back_on_it: 'Pushed Back',
  mentioned_in_passing: 'In Passing',
  recommending_against_it: 'Against',
};

const POSITION_ORDER: Record<ClientPosition, number> = {
  using_it: 1,
  considering_it: 2,
  asking_about_it: 3,
  mentioned_in_passing: 4,
  pushed_back_on_it: 5,
  recommending_against_it: 6,
};

export function StrategyLandscape() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [landscape, setLandscape] = useState<ClientStrategyLandscape | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Grid view for all clients
  const [allLandscapes, setAllLandscapes] = useState<ClientStrategyLandscape[]>([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [viewMode, setViewMode] = useState<'single' | 'grid'>('grid');

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    if (viewMode === 'grid' && clients.length > 0) {
      loadAllLandscapes();
    }
  }, [viewMode, clients]);

  useEffect(() => {
    if (selectedClientId && viewMode === 'single') {
      loadLandscape(selectedClientId);
    }
  }, [selectedClientId, viewMode]);

  const loadClients = async () => {
    try {
      const clientList = await api.listClients();
      setClients(clientList);
    } catch {
      // Silent fail
    }
  };

  const loadLandscape = async (clientId: string) => {
    setLoading(true);
    setError('');
    try {
      const result = await api.getClientStrategyLandscape(clientId);
      setLandscape(result);
    } catch (err) {
      setError('Failed to load strategy landscape');
    } finally {
      setLoading(false);
    }
  };

  const loadAllLandscapes = async () => {
    setLoadingAll(true);
    setError('');
    try {
      // Load landscape for each client
      const landscapes: ClientStrategyLandscape[] = [];
      for (const client of clients) {
        try {
          const result = await api.getClientStrategyLandscape(client.client_id);
          if (result.strategies.length > 0) {
            landscapes.push(result);
          }
        } catch {
          // Skip clients with errors
        }
      }
      // Sort by total mention count descending
      landscapes.sort((a, b) => {
        const aTotal = a.strategies.reduce((sum, s) => sum + s.mention_count, 0);
        const bTotal = b.strategies.reduce((sum, s) => sum + s.mention_count, 0);
        return bTotal - aTotal;
      });
      setAllLandscapes(landscapes);
    } catch (err) {
      setError('Failed to load landscapes');
    } finally {
      setLoadingAll(false);
    }
  };

  const getPositionClass = (position: ClientPosition) => {
    const classes: Record<ClientPosition, string> = {
      using_it: 'tag-position-using',
      considering_it: 'tag-position-considering',
      asking_about_it: 'tag-position-asking',
      pushed_back_on_it: 'tag-position-pushback',
      mentioned_in_passing: 'tag-position-passing',
      recommending_against_it: 'tag-position-against',
    };
    return `tag ${classes[position] || 'tag-default'}`;
  };

  const getInterestClass = (interest: InterestLevel) => {
    const classes: Record<InterestLevel, string> = {
      high: 'tag-blocker',
      medium: 'tag-strong-want',
      low: 'tag-nice-to-have',
      unclear: 'tag-unclear',
    };
    return `tag ${classes[interest] || 'tag-default'}`;
  };

  const getSentimentClass = (sentiment: Sentiment) => {
    const classes: Record<Sentiment, string> = {
      positive: 'tag-valid',
      negative: 'tag-invalid',
      neutral: 'tag-default',
      mixed: 'tag-warning',
    };
    return `tag ${classes[sentiment] || 'tag-default'}`;
  };

  const sortStrategies = (strategies: ClientStrategyLandscape['strategies']) => {
    return [...strategies].sort((a, b) => {
      // First by position priority
      const posA = POSITION_ORDER[a.latest_position] || 99;
      const posB = POSITION_ORDER[b.latest_position] || 99;
      if (posA !== posB) return posA - posB;
      // Then by mention count
      return b.mention_count - a.mention_count;
    });
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Strategy Landscape</h1>
        <p className="page-subtitle">Client-by-client view of strategy discussions</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* View Mode Toggle */}
      <div className="flex items-center gap-1 mb-1">
        <div className="btn-group">
          <button
            className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('grid')}
          >
            All Clients
          </button>
          <button
            className={`btn ${viewMode === 'single' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('single')}
          >
            Single Client
          </button>
        </div>
      </div>

      {viewMode === 'single' && (
        <div className="filters-bar">
          <div className="filter-group">
            <label className="form-label">Client</label>
            <select
              className="form-select"
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
            >
              <option value="">Select a client...</option>
              {clients.map((c) => (
                <option key={c.client_id} value={c.client_id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Grid View: All Clients */}
      {viewMode === 'grid' && (
        <>
          {loadingAll ? (
            <div className="loading">Loading all client landscapes...</div>
          ) : allLandscapes.length === 0 ? (
            <div className="empty-state">
              <p>No strategy discussions found across any clients.</p>
            </div>
          ) : (
            <div className="landscape-grid">
              {allLandscapes.map((landscape) => (
                <div key={landscape.client_id} className="landscape-card">
                  <div className="landscape-card-header">
                    <h3
                      className="landscape-client-name"
                      onClick={() => navigate(`/clients/${landscape.client_id}`)}
                    >
                      {landscape.client_name}
                    </h3>
                    <span className="text-small text-muted">
                      {landscape.strategies.reduce((sum, s) => sum + s.mention_count, 0)} mentions
                    </span>
                  </div>
                  <div className="landscape-strategies">
                    {sortStrategies(landscape.strategies).map((strategy) => (
                      <div key={strategy.strategy} className="landscape-strategy-row">
                        <div className="landscape-strategy-name">
                          {STRATEGY_LABELS[strategy.strategy]}
                          {strategy.has_end_client_demand && (
                            <span className="tag tag-end-client" style={{ marginLeft: '0.5rem', fontSize: '0.65rem' }}>
                              E-C
                            </span>
                          )}
                        </div>
                        <div className="landscape-strategy-tags">
                          <span className={getPositionClass(strategy.latest_position)}>
                            {POSITION_LABELS[strategy.latest_position]}
                          </span>
                          <span className={getInterestClass(strategy.latest_interest)}>
                            {strategy.latest_interest}
                          </span>
                        </div>
                        <div className="landscape-strategy-meta">
                          <span className="text-small text-muted">
                            {strategy.mention_count}x
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Single Client View */}
      {viewMode === 'single' && (
        <>
          {!selectedClientId ? (
            <div className="empty-state">
              <p>Select a client to view their strategy landscape.</p>
            </div>
          ) : loading ? (
            <div className="loading">Loading...</div>
          ) : !landscape ? (
            <div className="empty-state">
              <p>No strategy landscape data available.</p>
            </div>
          ) : landscape.strategies.length === 0 ? (
            <div className="empty-state">
              <p>No strategy discussions found for this client.</p>
            </div>
          ) : (
            <div>
              {/* Summary Stats */}
              <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
                <div className="stat-card">
                  <div className="stat-value">{landscape.strategies.length}</div>
                  <div className="stat-label">Strategies Discussed</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">
                    {landscape.strategies.reduce((sum, s) => sum + s.mention_count, 0)}
                  </div>
                  <div className="stat-label">Total Mentions</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">
                    {landscape.strategies.filter((s) => s.latest_position === 'using_it').length}
                  </div>
                  <div className="stat-label">Currently Using</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">
                    {landscape.strategies.filter((s) => s.has_end_client_demand).length}
                  </div>
                  <div className="stat-label">End-Client Demand</div>
                </div>
              </div>

              {/* Detailed Strategy List */}
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Strategy</th>
                      <th>Position</th>
                      <th>Interest</th>
                      <th>Sentiment</th>
                      <th>Mentions</th>
                      <th>First Mentioned</th>
                      <th>Last Mentioned</th>
                      <th>End-Client</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortStrategies(landscape.strategies).map((strategy) => (
                      <tr key={strategy.strategy}>
                        <td>
                          <strong>{STRATEGY_LABELS[strategy.strategy]}</strong>
                        </td>
                        <td>
                          <span className={getPositionClass(strategy.latest_position)}>
                            {POSITION_LABELS[strategy.latest_position]}
                          </span>
                        </td>
                        <td>
                          <span className={getInterestClass(strategy.latest_interest)}>
                            {strategy.latest_interest}
                          </span>
                        </td>
                        <td>
                          <span className={getSentimentClass(strategy.latest_sentiment)}>
                            {strategy.latest_sentiment}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>{strategy.mention_count}</td>
                        <td>{strategy.first_mentioned}</td>
                        <td>{strategy.last_mentioned}</td>
                        <td style={{ textAlign: 'center' }}>
                          {strategy.has_end_client_demand ? (
                            <span className="tag tag-end-client">Yes</span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Link to Mentions */}
              <div className="mt-1">
                <button
                  className="btn btn-secondary"
                  onClick={() =>
                    navigate(`/strategy-mentions?client_id=${landscape.client_id}`)
                  }
                >
                  View All Mentions for {landscape.client_name}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
